import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FlaskConical, BarChart3, Sliders, Download, Plus, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { exportToCSV } from '../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  tabs: { display: 'flex', gap: 0, marginBottom: '2rem', borderBottom: '1px solid rgba(242,237,228,0.1)' },
  tab: (active) => ({
    fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', padding: '10px 20px', cursor: 'pointer',
    background: 'none', border: 'none', color: active ? 'var(--primary)' : 'var(--secondary)',
    borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent', fontWeight: active ? 600 : 400,
  }),
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' },
  label: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.8rem', marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(242,237,228,0.15)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(242,237,228,0.15)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(242,237,228,0.15)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', minHeight: 60, resize: 'vertical', boxSizing: 'border-box' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  chartTitle: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.1rem', marginBottom: '1rem' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
    background: `${color}22`, color,
  }),
  sliderWrap: { marginBottom: '1.25rem' },
  slider: { width: '100%', accentColor: 'var(--primary)' },
  resultBox: { background: 'rgba(141,184,122,0.08)', border: '1px solid rgba(141,184,122,0.2)', borderRadius: 10, padding: '1.25rem', marginTop: '1rem' },
  resultLabel: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.8rem', marginBottom: 2 },
  resultValue: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.5rem' },
};

const experiments = [
  {
    id: 1, name: '"Your usual?" Push Reminder', channel: 'Push', status: 'Completed',
    hypothesis: 'Personalized push notifications referencing order history will increase visit frequency',
    audienceSize: 400, duration: '14 days',
    controlVisitFreq: 2.1, variantVisitFreq: 2.56, lift: 22, pValue: 0.003, ci: '[15%, 29%]',
    significant: true,
  },
  {
    id: 2, name: 'Matcha Cross-Sell Email', channel: 'Email', status: 'Running',
    hypothesis: 'Espresso customers shown matcha content will trial at higher rates',
    audienceSize: 300, duration: '21 days',
    controlVisitFreq: null, variantVisitFreq: null, lift: null, pValue: null, ci: null,
    significant: null,
  },
  {
    id: 3, name: 'Weekend 2x Points SMS', channel: 'SMS', status: 'Draft',
    hypothesis: 'Double points on weekends will shift visit patterns',
    audienceSize: 500, duration: '28 days',
    controlVisitFreq: null, variantVisitFreq: null, lift: null, pValue: null, ci: null,
    significant: null,
  },
];

const liftChartData = [
  { group: 'Control', visitFreq: 2.10 },
  { group: 'Variant', visitFreq: 2.56 },
];

export default function TestAndLearn() {
  const [tab, setTab] = useState('tracker');
  const [holdoutPct, setHoldoutPct] = useState(10);
  const [expectedLift, setExpectedLift] = useState(15);
  const [form, setForm] = useState({ name: '', channel: 'Email', hypothesis: '', control: '', variant: '', audienceSize: '', duration: '' });

  const monthlyRevenue = 55000;
  const treatedPct = 100 - holdoutPct;
  const incrementalRev = Math.round(monthlyRevenue * (treatedPct / 100) * (expectedLift / 100));

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Experiment created (demo mode)');
    setForm({ name: '', channel: 'Email', hypothesis: '', control: '', variant: '', audienceSize: '', duration: '' });
  };

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Test & Learn</h1>
      <p style={s.desc}>Design experiments, track results, and measure incrementality.</p>

      <div style={s.tabs}>
        <button style={s.tab(tab === 'builder')} onClick={() => setTab('builder')}><FlaskConical size={14} style={{ marginRight: 6, verticalAlign: -2 }} />A/B Test Builder</button>
        <button style={s.tab(tab === 'tracker')} onClick={() => setTab('tracker')}><BarChart3 size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Experiment Tracker</button>
        <button style={s.tab(tab === 'holdout')} onClick={() => setTab('holdout')}><Sliders size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Holdout Manager</button>
      </div>

      {tab === 'builder' && (
        <div style={s.card}>
          <form onSubmit={handleSubmit}>
            <div style={s.formGrid}>
              <div>
                <label style={s.label}>Test Name</label>
                <input style={s.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Weekend loyalty push" />
              </div>
              <div>
                <label style={s.label}>Channel</label>
                <select style={s.select} value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
                  <option>Email</option><option>SMS</option><option>Push</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={s.label}>Hypothesis</label>
              <textarea style={s.textarea} value={form.hypothesis} onChange={e => setForm({ ...form, hypothesis: e.target.value })} placeholder="We believe that..." />
            </div>
            <div style={s.formGrid}>
              <div>
                <label style={s.label}>Control Description</label>
                <textarea style={s.textarea} value={form.control} onChange={e => setForm({ ...form, control: e.target.value })} placeholder="No message / standard message" />
              </div>
              <div>
                <label style={s.label}>Variant Description</label>
                <textarea style={s.textarea} value={form.variant} onChange={e => setForm({ ...form, variant: e.target.value })} placeholder="Personalized push with order history" />
              </div>
            </div>
            <div style={s.formGrid}>
              <div>
                <label style={s.label}>Audience Size</label>
                <input style={s.input} type="number" value={form.audienceSize} onChange={e => setForm({ ...form, audienceSize: e.target.value })} placeholder="400" />
              </div>
              <div>
                <label style={s.label}>Duration (days)</label>
                <input style={s.input} type="number" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} placeholder="14" />
              </div>
            </div>
            <button type="submit" style={s.btn}><Plus size={16} /> Create Experiment</button>
          </form>
        </div>
      )}

      {tab === 'tracker' && (
        <>
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={s.chartTitle}>Experiments</div>
              <button style={s.btnOutline} onClick={() => exportToCSV(experiments, 'experiments')}>
                <Download size={14} /> Export CSV
              </button>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Channel</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Audience</th>
                  <th style={s.th}>Duration</th>
                  <th style={s.th}>Control Freq</th>
                  <th style={s.th}>Variant Freq</th>
                  <th style={s.th}>Lift</th>
                  <th style={s.th}>p-value</th>
                  <th style={s.th}>CI</th>
                </tr>
              </thead>
              <tbody>
                {experiments.map((exp) => (
                  <tr key={exp.id}>
                    <td style={s.td}>{exp.name}</td>
                    <td style={s.td}>{exp.channel}</td>
                    <td style={s.td}>
                      <span style={s.badge(exp.status === 'Completed' ? 'var(--primary)' : exp.status === 'Running' ? 'var(--secondary)' : 'var(--text)')}>
                        {exp.status}
                      </span>
                    </td>
                    <td style={s.td}>{exp.audienceSize}</td>
                    <td style={s.td}>{exp.duration}</td>
                    <td style={s.td}>{exp.controlVisitFreq ?? '—'}</td>
                    <td style={s.td}>{exp.variantVisitFreq ?? '—'}</td>
                    <td style={{ ...s.td, color: exp.lift ? 'var(--primary)' : 'var(--text)', fontWeight: exp.lift ? 600 : 400 }}>
                      {exp.lift != null ? `+${exp.lift}%` : '—'}
                    </td>
                    <td style={s.td}>{exp.pValue != null ? exp.pValue.toFixed(3) : '—'}</td>
                    <td style={s.td}>{exp.ci ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={s.card}>
            <div style={s.chartTitle}>"Your usual?" — Control vs Variant Visit Frequency</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
              <CheckCircle size={16} style={{ color: 'var(--primary)' }} />
              <span style={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--primary)', fontSize: '0.85rem', fontWeight: 600 }}>
                Statistically significant (p = 0.003)
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={liftChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,237,228,0.08)" />
                <XAxis dataKey="group" tick={{ fill: 'var(--text)', fontSize: 13, fontFamily: 'DM Sans' }} />
                <YAxis domain={[0, 3]} tick={{ fill: 'var(--secondary)', fontSize: 11, fontFamily: 'DM Sans' }} />
                <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid rgba(242,237,228,0.1)', borderRadius: 8, fontFamily: 'DM Sans', color: 'var(--text)' }} />
                <Bar dataKey="visitFreq" radius={[8, 8, 0, 0]} barSize={80}>
                  <Cell fill="rgba(200,169,110,0.6)" />
                  <Cell fill="var(--primary)" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {tab === 'holdout' && (
        <div style={s.card}>
          <div style={s.chartTitle}>Incrementality Calculator</div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            Estimate the incremental revenue impact of your marketing by adjusting holdout size and expected lift.
          </p>

          <div style={s.sliderWrap}>
            <label style={s.label}>Holdout %: {holdoutPct}%</label>
            <input type="range" min={5} max={30} value={holdoutPct} onChange={e => setHoldoutPct(Number(e.target.value))} style={s.slider} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'DM Sans', fontSize: '0.75rem', color: 'var(--secondary)' }}>
              <span>5%</span><span>30%</span>
            </div>
          </div>

          <div style={s.sliderWrap}>
            <label style={s.label}>Expected Lift %: {expectedLift}%</label>
            <input type="range" min={1} max={40} value={expectedLift} onChange={e => setExpectedLift(Number(e.target.value))} style={s.slider} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'DM Sans', fontSize: '0.75rem', color: 'var(--secondary)' }}>
              <span>1%</span><span>40%</span>
            </div>
          </div>

          <div style={s.resultBox}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <div>
                <div style={s.resultLabel}>Treated Group</div>
                <div style={s.resultValue}>{treatedPct}%</div>
              </div>
              <div>
                <div style={s.resultLabel}>Holdout Group</div>
                <div style={s.resultValue}>{holdoutPct}%</div>
              </div>
              <div>
                <div style={s.resultLabel}>Est. Incremental Revenue / mo</div>
                <div style={s.resultValue}>${incrementalRev.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
